/* File: frontend/src/components/dashboard/MyTasksWidget.js */
/* Version: 5.0 (Platinum Polish - Visual Consistency) */
/* Date: 2025-12-10 13:00:00 EST */
/* Description: 
 * - UI: Aligned with Platinum Design System (Shadows, Fonts, Hover effects).
 * - UX: Custom scrollbar and interactive list items.
 * - LOGIC: Preserved correct React Router v6 navigation from v4.0.
 * - ALERT: High-visibility styling for Reputation Alerts.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle, FileText, ArrowRight } from 'lucide-react';

// ── ПАЛИТРА ЭКРАНА «СЕГОДНЯ» → §3.1 (Ф1, quick-260909-jl8) ─────────────────
// Откат в каждой строке — ПРЕЖНИЙ литерал ЭТОГО ЖЕ места. На fitenko токен
// объявлен (styles/fitenko-tokens.css, селектор html.fitenko) и берётся он;
// на MLG и ELN токена НЕТ — берётся откат, то есть РОВНО прежний цвет.
// 🔴 Значения отката менять нельзя: ими доказывается неизменность чужих арендаторов.
// 🔴 Голый var(--ink) запрещён: у MLG он разрешается в ПУСТОТУ (отсутствие цвета),
//    а не в другой цвет — объявление становится недействительным.
// Класс C (подложки чипов) — по решению владельца от 2026-09-09, вариант B:
// подложка выводится правилом color-mix(in srgb, АКЦЕНТ 14%, --surface); правило
// объявлено ОДИН раз в pages/Dashboard.css под селектором html.fitenko. Бирюза —
// вариант T1: своего тона в §3.1 нет, берётся --green-accent.
const P = {
    canvas:      'var(--canvas, #f8fafc)',  // класс A
    ink:         'var(--ink, #1e293b)',  // класс A
    line:        'var(--line, #e2e8f0)',  // класс A
    lineStrong:  'var(--line, #cbd5e1)',  // класс A
    muted:       'var(--ink-muted, #64748b)',  // класс A
    mutedDeep:   'var(--ink-muted, #475569)',  // класс A
    mutedSoft:   'var(--ink-muted, #94a3b8)',  // класс A
    surfaceSoft: 'var(--surface-soft, #f1f5f9)',  // класс A
    blue1:       'var(--blue-accent, #3b82f6)',  // класс B
    green1:      'var(--green-accent, #22c55e)',  // класс B
    red1:        'var(--red-accent, #dc2626)',  // класс B
    red2:        'var(--red-accent, #ef4444)',  // класс B
    red3:        'var(--red-accent, #991b1b)',  // класс B
    red6:        'var(--red-accent, #b91c1c)',  // класс B
    chipGreen1:  'var(--chip-green-bg, #f0fdf4)',  // класс C
    chipRed1:    'var(--chip-red-bg, #fee2e2)',  // класс C
    chipRed5:    'var(--chip-red-bg, #fef2f2)',  // класс C
};




const MyTasksWidget = ({ tasks }) => {
    const navigate = useNavigate();

    const handleNavigation = (task) => {
        if (task.type === 'reputation_alert') {
            // Pass state to open specific tab on destination
            navigate('/reputation', { state: { initialTab: 'analytics' } });
        } else if (task.link) {
            navigate(task.link);
        } else if (task.case_id) {
            navigate(`/cases/${task.case_id}`);
        } else {
            navigate('/tasks');
        }
    };

    // Common Card Styles
    const cardStyle = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: `1px solid ${P.surfaceSoft}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    // Empty State
    if (!tasks || tasks.length === 0) {
        return (
            <div className="card" style={cardStyle}>
                <div className="card-header" style={{ padding: '24px', borderBottom: `1px solid ${P.canvas}` }}>
                    <h3 className="card-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: P.ink, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <CheckCircle size={20} style={{ stroke: P.green1 }}/> Active Tasks
                    </h3>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: P.mutedSoft, gap: '12px' }}>
                    <div style={{ background: P.chipGreen1, padding: '16px', borderRadius: '50%' }}>
                        <CheckCircle size={32} opacity={0.5} style={{ stroke: P.green1 }} />
                    </div>
                    <span>All caught up! No active tasks.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="card" style={cardStyle}>
            {/* Inject Custom Styles */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: ${P.surfaceSoft}; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: ${P.lineStrong}; borderRadius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: ${P.mutedSoft}; }
                
                .task-item-hover { transition: all 0.2s ease; border-left: 3px solid transparent; }
                .task-item-hover:hover { background-color: ${P.canvas}; transform: translateX(4px); }
            `}</style>

            {/* HEADER */}
            <div className="card-header" style={{
                padding: '20px 24px',
                borderBottom: `1px solid ${P.canvas}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h3 className="card-title" style={{
                    margin: 0, fontSize: '1.1rem', fontWeight: '700', color: P.ink,
                    display: 'flex', alignItems: 'center', gap: '10px'
                }}>
                    <CheckCircle size={20} style={{ stroke: P.green1 }}/> Active Tasks
                </h3>
                <span style={{
                    background: P.surfaceSoft, color: P.mutedDeep, 
                    padding: '4px 10px', borderRadius: '20px', 
                    fontSize: '0.75rem', fontWeight: '600'
                }}>
                    {tasks.length} pending
                </span>
            </div>
            
            {/* SCROLLABLE LIST */}
            <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
                {tasks.map((task) => {
                    const isAlert = task.type === 'reputation_alert';
                    
                    // Dynamic Styles
                    let bg = 'white';
                    let borderLeft = '3px solid transparent';
                    let iconBg = P.surfaceSoft;
                    let iconColor = P.muted;

                    if (isAlert) {
                        bg = P.chipRed5; // Pastel Red
                        borderLeft = `3px solid ${P.red2}`; // Bright Red Border
                        iconBg = P.chipRed1;
                        iconColor = P.red1;
                    }

                    return (
                        <div 
                            key={task.id} 
                            className="task-item-hover"
                            style={{
                                cursor: 'pointer',
                                padding: '16px 24px',
                                borderBottom: `1px solid ${P.surfaceSoft}`,
                                background: bg,
                                borderLeft: borderLeft,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px'
                            }}
                            onClick={() => handleNavigation(task)}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                {/* Icon */}
                                <div style={{
                                    width: '40px', height: '40px', borderRadius: '10px',
                                    background: iconBg, color: iconColor,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    {isAlert ? <AlertTriangle size={20}/> : <FileText size={20} style={{ stroke: P.blue1 }}/>}
                                </div>

                                {/* Text */}
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <h4 style={{ 
                                        margin: '0 0 2px 0', fontSize: '0.95rem', fontWeight: '600', 
                                        color: isAlert ? P.red3 : P.ink 
                                    }}>
                                        {task.title}
                                    </h4>
                                    <p style={{ 
                                        margin: 0, fontSize: '0.8rem', 
                                        color: isAlert ? P.red6 : P.muted 
                                    }}>
                                        {task.subtitle || task.description}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Action Button (Small) */}
                            {task.action_label && (
                                <button style={{
                                    background: isAlert ? P.red1 : 'white',
                                    color: isAlert ? 'white' : P.mutedDeep,
                                    border: isAlert ? 'none' : `1px solid ${P.line}`,
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    display: 'flex', alignItems: 'center', gap: '4px'
                                }}>
                                    {task.action_label} {isAlert && <ArrowRight size={12}/>}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

MyTasksWidget.propTypes = { tasks: PropTypes.array };
export default MyTasksWidget;